import _ from 'lodash';
import { useState, useCallback, useEffect } from 'react';
import { GridCol } from '@m/alchemy-ui/GridCol';
import { GridRow } from '@m/alchemy-ui/GridRow';
import { Toast } from '@m/alchemy-ui/Toast';
import { AreaOfExcellenceCheckbox } from 'settings/components/AreaOfExcellenceCheckbox';

import {
  useToasts,
  generateAutoDismissMessage
} from 'settings/hooks/useToasts';
import { useAreasOfExcellence } from 'settings/hooks/useAreasOfExcellence';

import * as Styled from './AreasOfExcellence.styled';

type AreaOfExcellence = ReturnType<
  typeof useAreasOfExcellence
>['areasOfExcellence'][number];

function useAreasOfExcellenceList(
  activeAreasOfExcellence: Array<AreaOfExcellence>
) {
  const NUMBER_OF_AOE = 6;
  const [items, setItems] = useState<AreaOfExcellence[]>([]);
  const isComplete = items.length >= NUMBER_OF_AOE;

  const reset = useCallback(() => setItems(activeAreasOfExcellence), [
    activeAreasOfExcellence,
  ]);

  const select = useCallback(
    (aoe: AreaOfExcellence) => {
      const newAoe = _.xorBy(items, [aoe], 'id');
      if (newAoe.length <= NUMBER_OF_AOE) {
        setItems(newAoe);
      }
    },
    [items]
  );

  useEffect(() => {
    if (activeAreasOfExcellence.length > 0) {
      setItems(activeAreasOfExcellence);
    }
  }, [activeAreasOfExcellence]);

  return { reset, select, items, isComplete };
}

function AreasOfExcellenceList({
  onSelect,
  areasOfExcellence,
  activeAreasOfExcellence,
}) {
  const disableCheckboxes = activeAreasOfExcellence.length >= 6;

  return (
    <Styled.GridWithMargin>
      <GridRow>
        {areasOfExcellence.map(areaOfExcellence => {
          const { id, caption } = areaOfExcellence;
          const isActive = Boolean(
            activeAreasOfExcellence.find(
              activeAreaOfExcellence => activeAreaOfExcellence.id === id
            )
          );

          return (
            <GridCol key={id} span={4}>
              <AreaOfExcellenceCheckbox
                label={caption}
                isChecked={isActive}
                onChange={() => onSelect(areaOfExcellence)}
                disabled={disableCheckboxes}
              />
            </GridCol>
          );
        })}
      </GridRow>
    </Styled.GridWithMargin>
  );
}

export function AreasOfExcellence() {
  const { messages, showMessage } = useToasts();

  const {
    loading,
    updating,
    updateAoe,
    areasOfExcellence,
    activeAreasOfExcellence,
  } = useAreasOfExcellence();

  const { items, reset, select, isComplete } = useAreasOfExcellenceList(
    activeAreasOfExcellence
  );

  const showSuccessUpdateToast = () => {
    // prettier-ignore
    const message = generateAutoDismissMessage({ content: 'Selection saved.', level: 'confirm', placement: 'top' });
    showMessage(message);
  };

  const showErrorUpdateToast = () => {
    // prettier-ignore
    const message = generateAutoDismissMessage({ content: 'There was a problem saving the selection. Please try again later.', level: 'error', placement: 'top' });
    showMessage(message);
  };

  const handleSave = () => {
    updateAoe(items).then(showSuccessUpdateToast).catch(showErrorUpdateToast);
  };

  return (
    <>
      <Styled.HelpText>
        <strong>Must select 6 Areas of Excellence</strong> that align to
        behaviors you are trying to drive with your team. These are the Areas of
        Excellence that will appear within every feedback request.
      </Styled.HelpText>

      {loading && <Styled.Loading inline />}

      {!loading && (
        <AreasOfExcellenceList
          onSelect={select}
          activeAreasOfExcellence={items}
          areasOfExcellence={areasOfExcellence}
        />
      )}

      <Styled.Note>
        Areas of Excellence can be customized for your company. Contact your
        Client Success Manager to discuss other options.
      </Styled.Note>

      <Styled.Footer>
        <Styled.Button priority="secondary" onClick={reset}>
          Reset
        </Styled.Button>

        <Styled.Button
          priority="primary"
          onClick={handleSave}
          disabled={!isComplete}
          isLoading={updating}
        >
          Save
        </Styled.Button>
      </Styled.Footer>

      <Toast messages={messages} />
    </>
  );
}
